// CreateMemberConnectionTypes.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import cls from "./CreateMemberConnectionTypes.module.scss";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";
import { CONNECTION_TYPES } from "@/enums/lifestyleEnums";

/* Local hero art */
import mrImg from "@/assets/UI/memberCreate/q9Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q9MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q9Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q9MrsM.webp";

/* Animations (Welcome vibe) */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 1, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 1, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

function useMemberLayoutContext() {
  return useOutletContext();
}

const CONNECTION_META = {
  emotional_physical: { emoji: "❤️" },
  flirty_fun: { emoji: "😉" },
  lifestyle_swing: { emoji: "♻️" },
  deep_sensual_friendship: { emoji: "💞" },
  online_only: { emoji: "📱" },
};

function getPersonaKey(rawGender, isCouple) {
  if (isCouple) return "couple";
  if (rawGender === "man" || rawGender === "trans_man") return "man";
  if (rawGender === "woman" || rawGender === "trans_woman") return "woman";
  return "other";
}

export default function CreateMemberConnectionTypes() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = isCoupleCtx || memberType === "couple";

  const t = (k, o) => i18n.t(k, o);

  const [submitting, setSubmitting] = useState(false);
  const [choices, setChoices] = useState([]);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const title = t("onboarding.connection_types.title", {
    defaultValue: "Connection Vibes",
  });

  const personaKey = getPersonaKey(rawGender, isCouple);

  const introLead = t(`onboarding.connection_types.intros.${personaKey}.lead`, {
    defaultValue: "Tell us what kind of connections you’re into.",
  });
  const introLine1 = t(
    `onboarding.connection_types.intros.${personaKey}.line1`,
    { defaultValue: "Pick as many as you want." },
  );
  const introLine2 = t(
    `onboarding.connection_types.intros.${personaKey}.line2`,
    { defaultValue: "This helps us match you faster." },
  );
  const introLine3 = t(
    `onboarding.connection_types.intros.${personaKey}.line3`,
    { defaultValue: "No pressure. You can always update later." },
  );

  const guideTitle = t("onboarding.connection_types.guide.title", {
    defaultValue: "Quick tip",
  });
  const guideBody = t("onboarding.connection_types.guide.body", {
    defaultValue:
      "If you’re not sure, pick what feels fun right now. You can refine later.",
  });

  const options = (CONNECTION_TYPES || []).map((value) => ({
    value,
    emoji: CONNECTION_META[value]?.emoji || "✨",
    title: t(`onboarding.connection_types.options.${value}.label`, {
      defaultValue: value,
    }),
    hint: t(`onboarding.connection_types.tapToSelect.${value}`, {
      defaultValue: "Tap to add this vibe to your attraction mix",
    }),
  }));

  const hasChoices = choices.length > 0;

  function toggleChoice(current, value) {
    if (current.includes(value)) return current.filter((v) => v !== value);
    return [...current, value];
  }

  async function onSubmit(e) {
    e.preventDefault();

    if (!choices.length) {
      toast.info(
        t("onboarding.connection_types.toast.member_required", {
          defaultValue: "Pick at least one option.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? { memberConnectionTypes: choices, partnerConnectionTypes: choices }
      : { memberConnectionTypes: choices };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/connection_types",
        payload,
      );
      await refreshMe();
      navigate(data?.next || "/member/intimacy_types", { replace: true });
    } catch (err) {
      console.error("update connection types failed:", err);
      toast.error(
        t("onboarding.connection_types.toast.save_error", {
          defaultValue: "Couldn’t save. Try again.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Scrolling content (page scrolls, background stays fixed) */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>
                <div className={cls.formSub}>
                  {introLead}
                  <br />
                  {introLine1}
                  <br />
                  {introLine2}
                  <br />
                  {introLine3}
                </div>
              </div>
            </div>

            <form className={cls.form} onSubmit={onSubmit} noValidate>
              <fieldset className={cls.options}>
                <legend className="sr-only">{title}</legend>

                {options.map((opt) => {
                  const checked = choices.includes(opt.value);

                  return (
                    <label
                      key={opt.value}
                      className={`${cls.option} ${
                        checked ? cls.optionActive : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        name="connectionTypes"
                        value={opt.value}
                        checked={checked}
                        onChange={() =>
                          setChoices((prev) => toggleChoice(prev, opt.value))
                        }
                      />

                      <span className={cls.optionBody}>
                        <span className={cls.optionEmoji} aria-hidden="true">
                          {opt.emoji}
                        </span>

                        <span className={cls.optionText}>
                          <strong>{opt.title}</strong>
                          <small>{opt.hint}</small>
                        </span>

                        <span className={cls.optionDot} aria-hidden="true" />
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              <div className={cls.aiWrap} role="status" aria-live="polite">
                <div
                  className={`${cls.aiNote} ${
                    hasChoices ? cls["ai-picked"] : cls["ai-empty"]
                  }`}
                >
                  <span className={cls.aiPulse} aria-hidden="true" />
                  <p className={cls.aiText}>
                    <span className={cls.aiStrong}>💡 {guideTitle}</span>
                    <br />
                    {guideBody}
                  </p>
                </div>
              </div>

              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitting || !hasChoices}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting
                    ? t("common.saving", { defaultValue: "Saving…" })
                    : t("common.continue", { defaultValue: "Continue" })}
                </button>
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
